import { GardenBedType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-bed-type.enum';
import { GardenSpaceErrorCode } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-space-error-code.enum';

/**
 * L'espace du potager côté écran (proposition du 28/10/2026, §2) : ce qu'il faut pour
 * dessiner et proposer une emprise en cases. Le verdict (« Ça tient », « C'est un peu
 * juste ») vient du back (`PlantApi.previewPlacement`) ; on ne calcule ici que ce qui
 * s'affiche avant lui : plants par case, cases nécessaires, bloc proposé.
 */

/** Case d'une planche sans réglage (et des planches sans dimensions). */
export const DEFAULT_CELL_CM = 30;
/** En cases, pas d'allée : un plant tous les x cm dans les deux sens, jamais moins de 7,5 cm. */
export const MIN_CELL_SPACING_CM = 7.5;
/** Une planche en cases garde au plus 20 cases de côté, comme l'ancien formulaire. */
export const MAX_CELLS_PER_SIDE = 20;
/** Deux plantes au plus vivent sur l'emprise d'une autre (règle du back, `MAX_ASSOCIATES_PER_HOST`). */
export const MAX_ASSOCIATES_PER_HOST = 2;

/** Emprise d'une plantation, en cases (x = colonne le long de la largeur, y = ligne). */
export interface Footprint {
  positionX: number;
  positionY: number;
  spanX: number;
  spanY: number;
}

export interface CellPos {
  x: number;
  y: number;
}

/** Une plantation posée sur la planche, telle que la renvoient le plan ou la liste des plantes. */
export interface PlacedPlant {
  plantUid?: string;
  uid?: string;
  positionX?: number | null;
  positionY?: number | null;
  spanX?: number | null;
  spanY?: number | null;
  hostPlantUid?: string | null;
  nbPlants?: number | null;
}

export const cellKey = (x: number, y: number): string => `${x},${y}`;

/** Écart d'un plant en cases, dans les deux sens ; `null` si la culture n'en a pas. */
export function cellSpacingCm(inRowCm: number | null | undefined): number | null {
  return inRowCm ? Math.max(inRowCm, MIN_CELL_SPACING_CM) : null;
}

/** Plants par case : 9 oignons, 16 carottes, 1 laitue ; 0 quand un pied prend plus d'une case. */
export function plantsPerCell(inRowCm: number | null | undefined, cellCm = DEFAULT_CELL_CM): number | null {
  const d = cellSpacingCm(inRowCm);
  return d ? Math.floor((cellCm * cellCm) / (d * d)) : null;
}

/** Cases pour un pied (tomate : 2,8), au dixième. */
export function cellsPerPlant(inRowCm: number | null | undefined, cellCm = DEFAULT_CELL_CM): number | null {
  const d = cellSpacingCm(inRowCm);
  return d ? Math.round(((d * d) / (cellCm * cellCm)) * 10) / 10 : null;
}

/** Cases nécessaires (50 oignons : 6) ; sans écart connu, une case par plant. */
export function cellsNeeded(nbPlants: number, inRowCm: number | null | undefined, cellCm = DEFAULT_CELL_CM): number {
  const nb = Math.max(1, nbPlants || 1);
  const d = cellSpacingCm(inRowCm);
  if (!d) return nb;
  return Math.max(1, Math.ceil((nb * d * d) / (cellCm * cellCm)));
}

/** Plants de chaque case, posés comme au jardin : 50 oignons sur 6 cases font 9, 9, 8, 8, 8, 8. */
export function spreadCounts(nbPlants: number, cells: number): number[] {
  if (cells <= 0) return [];
  const base = Math.floor(nbPlants / cells);
  const extra = nbPlants % cells;
  return Array.from({ length: cells }, (_, i) => base + (i < extra ? 1 : 0));
}

/** Cases couvertes par une emprise, ligne par ligne. */
export function footprintCells(fp: Footprint): CellPos[] {
  const cells: CellPos[] = [];
  for (let y = fp.positionY; y < fp.positionY + fp.spanY; y++) {
    for (let x = fp.positionX; x < fp.positionX + fp.spanX; x++) cells.push({ x, y });
  }
  return cells;
}

/** Le rectangle entre deux cases touchées, dans n'importe quel ordre. */
export function rectFromCorners(a: CellPos, b: CellPos): Footprint {
  return {
    positionX: Math.min(a.x, b.x),
    positionY: Math.min(a.y, b.y),
    spanX: Math.abs(a.x - b.x) + 1,
    spanY: Math.abs(a.y - b.y) + 1,
  };
}

/** Les cases communes à deux emprises ; `null` si elles ne se touchent pas. */
export function intersectFootprints(a: Footprint, b: Footprint): Footprint | null {
  const x = Math.max(a.positionX, b.positionX);
  const y = Math.max(a.positionY, b.positionY);
  const right = Math.min(a.positionX + a.spanX, b.positionX + b.spanX);
  const bottom = Math.min(a.positionY + a.spanY, b.positionY + b.spanY);
  return right > x && bottom > y ? { positionX: x, positionY: y, spanX: right - x, spanY: bottom - y } : null;
}

/** `inner` tient tout entière dans `outer` (une associée sur l'emprise de son hôte). */
export function containsFootprint(outer: Footprint, inner: Footprint): boolean {
  return (
    inner.positionX >= outer.positionX &&
    inner.positionY >= outer.positionY &&
    inner.positionX + inner.spanX <= outer.positionX + outer.spanX &&
    inner.positionY + inner.spanY <= outer.positionY + outer.spanY
  );
}

export function sameFootprint(a: Footprint | null | undefined, b: Footprint | null | undefined): boolean {
  if (!a || !b) return a === b;
  return a.positionX === b.positionX && a.positionY === b.positionY && a.spanX === b.spanX && a.spanY === b.spanY;
}

/** Emprise d'une plantation posée ; `null` si elle n'a pas de position (pot, hors plan). */
export function footprintOf(p: PlacedPlant): Footprint | null {
  if (p.positionX == null || p.positionY == null) return null;
  return { positionX: p.positionX, positionY: p.positionY, spanX: p.spanX || 1, spanY: p.spanY || 1 };
}

/**
 * Cases prises par les plantations principales de la planche. Une associée vit sur l'emprise
 * de son hôte et ne prend pas de place ; `exceptPlantUid` est la plantation qu'on déplace ou
 * qu'on étale (elle ne se gêne pas elle-même).
 */
export function occupiedCells(plants: PlacedPlant[], exceptPlantUid?: string | null): Set<string> {
  const taken = new Set<string>();
  for (const p of plants) {
    const uid = p.plantUid ?? p.uid;
    if (p.hostPlantUid || (exceptPlantUid && uid === exceptPlantUid)) continue;
    const fp = footprintOf(p);
    if (!fp) continue;
    for (const c of footprintCells(fp)) taken.add(cellKey(c.x, c.y));
  }
  return taken;
}

export function overlapsTaken(fp: Footprint, taken: Set<string>): boolean {
  return footprintCells(fp).some((c) => taken.has(cellKey(c.x, c.y)));
}

/**
 * Bloc proposé pour `needed` cases : le plus compact qui tient dans la place libre (3 × 2 pour
 * 6 cases plutôt que 6 × 1, plus large que haut à surface égale), posé à partir du premier coin
 * libre, ou sur la case touchée (`prefer`). Rien d'assez grand : le plus grand bloc libre, et le
 * verdict dira « C'est un peu juste ». Planche pleine : `null`.
 */
export function proposeFootprint(
  needed: number,
  columns: number,
  rows: number,
  taken: Set<string>,
  prefer?: CellPos | null,
): Footprint | null {
  if (columns < 1 || rows < 1) return null;
  const free = freeSums(columns, rows, taken);
  const isFree = (x: number, y: number, w: number, h: number) =>
    free[y + h][x + w] - free[y][x + w] - free[y + h][x] + free[y][x] === w * h;

  const shapes: { w: number; h: number }[] = [];
  for (let w = 1; w <= columns; w++) for (let h = 1; h <= rows; h++) shapes.push({ w, h });
  const enough = shapes
    .filter((s) => s.w * s.h >= needed)
    .sort((a, b) => a.w * a.h - b.w * b.h || Math.abs(a.w - a.h) - Math.abs(b.w - b.h) || b.w - a.w);
  const smaller = shapes
    .filter((s) => s.w * s.h < needed)
    .sort((a, b) => b.w * b.h - a.w * a.h || Math.abs(a.w - a.h) - Math.abs(b.w - b.h) || b.w - a.w);

  // Assez grand sur la case touchée, puis assez grand ailleurs, puis le plus grand possible.
  const passes: [typeof shapes, CellPos | null][] = prefer
    ? [[enough, prefer], [enough, null], [smaller, prefer], [smaller, null]]
    : [[enough, null], [smaller, null]];
  for (const [list, around] of passes) {
    for (const { w, h } of list) {
      for (const [x, y] of origins(w, h, columns, rows, around)) {
        if (isFree(x, y, w, h)) return { positionX: x, positionY: y, spanX: w, spanY: h };
      }
    }
  }
  return null;
}

/** Une forme de bloc, en cases (3 × 2). */
export interface BlockShape {
  w: number;
  h: number;
}

/**
 * Formes proposées en puces pour `needed` cases : celles de la plus petite surface suffisante
 * qui trouvent une place libre (3 × 2 et 2 × 3 pour 6 cases), plus larges d'abord, trois au plus.
 * Aucune n'a la place : les formes du plus grand bloc libre, et `enough` est faux (« Un peu juste »).
 */
export function shapesFor(needed: number, columns: number, rows: number, taken: Set<string>): { shapes: BlockShape[]; enough: boolean } {
  if (columns < 1 || rows < 1) return { shapes: [], enough: false };
  const free = freeSums(columns, rows, taken);
  const fits = (w: number, h: number): boolean => {
    for (let y = 0; y + h <= rows; y++) {
      for (let x = 0; x + w <= columns; x++) {
        if (free[y + h][x + w] - free[y][x + w] - free[y + h][x] + free[y][x] === w * h) return true;
      }
    }
    return false;
  };
  const ofArea = (a: number): BlockShape[] => {
    const list: BlockShape[] = [];
    for (let w = Math.min(columns, a); w >= 1; w--) {
      if (a % w === 0 && a / w <= rows && fits(w, a / w)) list.push({ w, h: a / w });
    }
    return list.sort((p, q) => Math.abs(p.w - p.h) - Math.abs(q.w - q.h) || q.w - p.w).slice(0, 3);
  };
  const max = columns * rows;
  for (let a = Math.max(1, needed); a <= max; a++) {
    const list = ofArea(a);
    if (list.length) return { shapes: list, enough: true };
  }
  for (let a = Math.min(needed - 1, max); a >= 1; a--) {
    const list = ofArea(a);
    if (list.length) return { shapes: list, enough: false };
  }
  return { shapes: [], enough: false };
}

/**
 * Où poser un bloc w × h quand on touche une case : son coin haut gauche sur la case, recalé
 * dans la planche ; s'il couvrirait une plantation, la place libre la plus proche. `null` si
 * la forme ne tient nulle part.
 */
export function placeShape(shape: BlockShape, at: CellPos, columns: number, rows: number, taken: Set<string>): Footprint | null {
  const { w, h } = shape;
  if (w > columns || h > rows) return null;
  const free = freeSums(columns, rows, taken);
  const ox = Math.min(Math.max(0, at.x), columns - w);
  const oy = Math.min(Math.max(0, at.y), rows - h);
  let best: Footprint | null = null;
  let bestDistance = Infinity;
  for (let y = 0; y + h <= rows; y++) {
    for (let x = 0; x + w <= columns; x++) {
      if (free[y + h][x + w] - free[y][x + w] - free[y + h][x] + free[y][x] !== w * h) continue;
      const distance = Math.abs(x - ox) + Math.abs(y - oy);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = { positionX: x, positionY: y, spanX: w, spanY: h };
      }
    }
  }
  return best;
}

/** Origines possibles d'un bloc w × h : celles qui couvrent la case touchée, sinon toutes, ligne par ligne. */
function origins(w: number, h: number, columns: number, rows: number, prefer?: CellPos | null): [number, number][] {
  const list: [number, number][] = [];
  for (let y = 0; y + h <= rows; y++) {
    for (let x = 0; x + w <= columns; x++) {
      if (!prefer || (prefer.x >= x && prefer.x < x + w && prefer.y >= y && prefer.y < y + h)) list.push([x, y]);
    }
  }
  return list;
}

/** Sommes cumulées des cases libres : un bloc se teste en quatre lectures. */
function freeSums(columns: number, rows: number, taken: Set<string>): number[][] {
  const sums = Array.from({ length: rows + 1 }, () => new Array<number>(columns + 1).fill(0));
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < columns; x++) {
      const isFree = taken.has(cellKey(x, y)) ? 0 : 1;
      sums[y + 1][x + 1] = isFree + sums[y][x + 1] + sums[y + 1][x] - sums[y][x];
    }
  }
  return sums;
}

/**
 * Plus petit bloc à garder quand une plantation a moins de plants (« Réduire sur N cases ») :
 * il part du même coin et reste dans l'emprise actuelle. `null` quand il n'y a rien à rendre.
 */
export function shrinkFootprint(current: Footprint, needed: number): Footprint | null {
  if (current.spanX * current.spanY <= needed) return null;
  let best: Footprint | null = null;
  for (let w = 1; w <= current.spanX; w++) {
    for (let h = 1; h <= current.spanY; h++) {
      if (w * h < needed) continue;
      const better =
        !best ||
        w * h < best.spanX * best.spanY ||
        (w * h === best.spanX * best.spanY && Math.abs(w - h) < Math.abs(best.spanX - best.spanY));
      if (better) best = { positionX: current.positionX, positionY: current.positionY, spanX: w, spanY: h };
    }
  }
  return best && best.spanX * best.spanY < current.spanX * current.spanY ? best : null;
}

/** Une planche telle que la renvoie la liste : sa grille, ses places et sa place libre (back). */
export interface BedShape {
  uid: string;
  type: GardenBedType;
  rows?: number | null;
  columns?: number | null;
  capacity?: number | null;
  freeCells?: number | null;
  freePlaces?: number | null;
}

/**
 * Place d'une planche, telle que le back la compte (même règle que le plan) : cases libres sur
 * cases de la grille, ou places libres sur places du pot.
 */
export function bedSpace(bed: BedShape): { free: number; total: number } {
  if (bed.type === GardenBedType.POT) {
    return { free: bed.freePlaces ?? 0, total: bed.capacity ?? 0 };
  }
  return { free: bed.freeCells ?? 0, total: (bed.columns ?? 0) * (bed.rows ?? 0) };
}

/**
 * Cases couvertes par les plantations principales actives d'une planche, pour colorer le
 * mini-aperçu de « Mon potager ». Le compte des cases libres, lui, vient du back.
 */
export function coveredCells(bed: BedShape, plants: (PlacedPlant & { gardenBedUid?: string | null; archivedAt?: unknown })[]): CellPos[] {
  const own = plants.filter((p) => p.gardenBedUid === bed.uid && !p.archivedAt);
  return [...occupiedCells(own)]
    .map((key) => {
      const [x, y] = key.split(',').map(Number);
      return { x, y };
    })
    .filter((c) => c.x < (bed.columns ?? 0) && c.y < (bed.rows ?? 0));
}

/** Clé i18n d'un refus de l'espace du potager (`error.data.code`) ; `null` pour une autre erreur. */
export function gardenSpaceErrorKey(error: unknown): string | null {
  const data = (error as { data?: Record<string, unknown> })?.data;
  const code = (data?.['code'] ?? (data?.['message'] as Record<string, unknown> | undefined)?.['code']) as string | undefined;
  switch (code) {
    case GardenSpaceErrorCode.FOOTPRINT_OVERLAP:
      return 'garden.space.errors.overlap';
    case GardenSpaceErrorCode.OUT_OF_BED:
      return 'garden.space.errors.out-of-bed';
    case GardenSpaceErrorCode.BED_NOT_EMPTY:
      return 'garden.space.errors.bed-not-empty';
    case GardenSpaceErrorCode.HOST_NOT_FOUND:
    case GardenSpaceErrorCode.HOST_ELSEWHERE:
    case GardenSpaceErrorCode.HOST_CHAIN:
    case GardenSpaceErrorCode.HOST_FULL:
    case GardenSpaceErrorCode.OUTSIDE_HOST:
      return 'garden.space.errors.host';
    case GardenSpaceErrorCode.ROWS_SPAN_REQUIRED:
    case GardenSpaceErrorCode.ROWS_DIMENSIONS_REQUIRED:
      return 'garden.space.errors.dimensions';
    case GardenSpaceErrorCode.GRID_TOO_LARGE:
      return 'garden.space.errors.grid-too-large';
    default:
      return null;
  }
}

/** Une longueur en mètres comme on l'écrit : « 1,20 m », « 4 m », « 0,90 m ». */
export function formatMetres(cm: number | null | undefined, lang = 'fr'): string {
  if (cm == null) return '';
  const whole = cm % 100 === 0;
  const value = new Intl.NumberFormat(lang.toLowerCase(), {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cm / 100);
  return `${value} m`;
}

/** Un nombre au dixième dans la langue de l'app : « 2,8 ». */
export function formatDecimal(value: number, lang = 'fr'): string {
  return new Intl.NumberFormat(lang.toLowerCase(), { maximumFractionDigits: 1 }).format(value);
}
